import { useEffect, useRef, useState } from "react";
import {
  Check,
  Copy,
  ExternalLink,
  Link2,
  Loader,
  Plus,
  RefreshCw,
} from "lucide-react";
import { formApi, type FormShortLink } from "./api";
import type { FeedbackForm } from "./contract";

export function FormSharing({
  form,
  canWrite,
}: {
  form: FeedbackForm;
  canWrite: boolean;
}) {
  const [links, setLinks] = useState<FormShortLink[]>([]),
    [loading, setLoading] = useState(true),
    [error, setError] = useState(""),
    [notice, setNotice] = useState(""),
    [slug, setSlug] = useState(""),
    [label, setLabel] = useState(""),
    [busy, setBusy] = useState(false),
    [retiring, setRetiring] = useState<string | null>(null),
    [editing, setEditing] = useState<string | null>(null),
    [editedLabel, setEditedLabel] = useState("");
  const requestId = useRef(crypto.randomUUID());
  const direct = `https://craftdownunder.co/forms/${form.id}`;
  async function refresh() {
    setLoading(true);
    try {
      setLinks((await formApi.links(form.id)).links);
      setError("");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not load short links.");
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    formApi
      .links(form.id)
      .then((r) => {
        if (!cancelled) {
          setLinks(r.links);
          setError("");
        }
      })
      .catch((e) => {
        if (!cancelled) setError(e.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [form.id]);
  async function copy(value: string) {
    try {
      await navigator.clipboard.writeText(value);
      setNotice("Link copied. Ready to share.");
    } catch {
      setNotice(`Copy this link: ${value}`);
    }
  }
  function replace(link: FormShortLink) {
    setLinks((now) => [link, ...now.filter((x) => x.path !== link.path)]);
  }
  async function create() {
    setBusy(true);
    setError("");
    setNotice("");
    try {
      const { link } = await formApi.createLink(form.id, {
        slug,
        label,
        request_id: requestId.current,
      });
      replace(link);
      setSlug("");
      setLabel("");
      requestId.current = crypto.randomUUID();
      setNotice(
        "Your short link is ready. Copy it below to invite your community.",
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not create link.");
    } finally {
      setBusy(false);
    }
  }
  async function retire(path: string) {
    setBusy(true);
    setError("");
    try {
      replace((await formApi.retireLink(form.id, path)).link);
      setRetiring(null);
      setNotice("Link retired. Its name will never be reassigned.");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not retire link.");
    } finally {
      setBusy(false);
    }
  }
  async function rename(path: string) {
    setBusy(true);
    setError("");
    try {
      replace((await formApi.labelLink(form.id, path, editedLabel)).link);
      setEditing(null);
      setNotice("Link label saved.");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not save label.");
    } finally {
      setBusy(false);
    }
  }
  const active = links.filter((link) => link.status === "active"),
    retired = links.filter((link) => link.status !== "active");
  function card(link: FormShortLink) {
    return (
      <div key={link.path} className="subcard p-4 space-y-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-sm font-medium break-all">
              {link.url.replace("https://", "")}
            </p>
            <p className="text-xs text-glass-400 mt-1">
              {link.label || "No label"} ·{" "}
              {link.status === "active"
                ? `${link.visits} visits`
                : "Retired · name reserved"}
            </p>
          </div>
          <div className="flex flex-wrap gap-1">
            {link.status === "active" && (
              <>
                <button
                  className="btn-ghost text-xs"
                  onClick={() => void copy(link.url)}
                  aria-label={`Copy ${link.path}`}
                >
                  <Copy className="w-3.5 h-3.5" />
                  Copy
                </button>
                <a
                  className="btn-ghost"
                  href={link.url}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={`Open ${link.path}`}
                >
                  <ExternalLink className="w-3.5 h-3.5" />
                </a>
              </>
            )}
            {canWrite && (
              <button
                className="btn-ghost text-xs"
                disabled={busy}
                onClick={() => {
                  setEditing(link.path);
                  setEditedLabel(link.label);
                }}
              >
                Edit label
              </button>
            )}
            {canWrite && link.status === "active" && (
              <button
                className="btn-ghost text-xs"
                disabled={busy}
                onClick={() => setRetiring(link.path)}
              >
                Retire
              </button>
            )}
          </div>
        </div>
        {editing === link.path && (
          <form
            className="flex flex-wrap gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              void rename(link.path);
            }}
          >
            <input
              autoFocus
              aria-label="Edit link label"
              className="glass-input flex-1 min-w-0"
              value={editedLabel}
              onChange={(e) => setEditedLabel(e.target.value)}
              maxLength={80}
            />
            <button className="glass-button-secondary text-xs" disabled={busy}>
              Save label
            </button>
            <button
              type="button"
              className="btn-ghost text-xs"
              onClick={() => setEditing(null)}
            >
              Cancel
            </button>
          </form>
        )}
        {retiring === link.path && (
          <div
            className="border-t border-glass-200/10 pt-3 space-y-3"
            role="alert"
          >
            <p className="text-xs text-glass-300">
              This link will stop opening the form immediately. Its name stays
              reserved permanently. Other links and responses are unaffected.
            </p>
            <div className="flex justify-end gap-2">
              <button
                className="btn-ghost text-xs"
                disabled={busy}
                onClick={() => setRetiring(null)}
              >
                Keep link
              </button>
              <button
                className="glass-button-secondary text-xs"
                disabled={busy}
                onClick={() => void retire(link.path)}
              >
                Retire this link
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }
  return (
    <div
      className="grid lg:grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)] gap-5"
      data-tour="forms-sharing"
    >
      <div className="space-y-5">
        <section className="glass-card p-5 space-y-4">
          <div className="flex items-center gap-3">
            <Link2 className="w-5 h-5 text-accent-300" />
            <h2 className="font-medium">Invite your community</h2>
          </div>
          <p className="text-sm text-glass-400">
            Every link opens this same Hub form. Players still sign in and link
            their Minecraft account before answering.
          </p>
          <label className="block text-xs space-y-2">
            <span className="text-glass-400">Direct Hub link</span>
            <input
              readOnly
              className="glass-input w-full"
              value={direct}
              onFocus={(e) => e.target.select()}
            />
          </label>
          <button
            className="glass-button-secondary text-xs"
            onClick={() => void copy(direct)}
          >
            <Copy className="w-4 h-4" />
            Copy Hub link
          </button>
          {form.status !== "published" && (
            <p className="text-xs text-amber-300">
              {form.status === "draft"
                ? "This draft is private. Publish it before inviting responses."
                : "Responses are closed. Reopen the form before inviting new responses."}
            </p>
          )}
        </section>
        {canWrite && (
          <section
            className="glass-card p-5 space-y-4"
            data-tour="forms-link-create"
          >
            <h2 className="font-medium text-sm">A shorter way to share</h2>
            <p className="text-xs text-glass-400">
              Choose a memorable name or leave it blank for an automatic one.
              Existing and retired names are always protected.
            </p>
            <form
              className="space-y-4"
              onSubmit={(e) => {
                e.preventDefault();
                void create();
              }}
            >
              <fieldset
                disabled={busy || form.status !== "published"}
                className="space-y-4"
              >
                <label className="block text-xs space-y-2">
                  <span className="text-glass-400">
                    Short-link name{" "}
                    <span className="font-normal">(optional)</span>
                  </span>
                  <div className="flex items-center glass-input p-0 overflow-hidden">
                    <span className="pl-3 pr-1 text-glass-400 whitespace-nowrap">
                      playcdu.co/f-
                    </span>
                    <input
                      className="bg-transparent px-2 py-2.5 min-w-0 flex-1 outline-none"
                      placeholder="spring-games"
                      maxLength={40}
                      pattern={"[a-z0-9]([a-z0-9\\-]{0,38}[a-z0-9])?"}
                      value={slug}
                      onChange={(e) => {
                        setSlug(e.target.value.toLowerCase());
                        requestId.current = crypto.randomUUID();
                      }}
                    />
                  </div>
                </label>
                <label className="block text-xs space-y-2">
                  <span className="text-glass-400">
                    Private label{" "}
                    <span className="font-normal">(optional)</span>
                  </span>
                  <input
                    className="glass-input w-full"
                    placeholder="Discord announcement, in-game chat…"
                    maxLength={80}
                    value={label}
                    onChange={(e) => {
                      setLabel(e.target.value);
                      requestId.current = crypto.randomUUID();
                    }}
                  />
                </label>
                <button className="btn-primary w-full" type="submit">
                  {busy ? (
                    <Loader className="w-4 h-4 animate-spin" />
                  ) : (
                    <Plus className="w-4 h-4" />
                  )}
                  Create short link
                </button>
              </fieldset>
            </form>
            {form.status !== "published" && (
              <p className="text-xs text-glass-400">
                Publish or reopen this form to create a short link.
              </p>
            )}
          </section>
        )}
        <p className="text-xs text-glass-400 px-1">
          Short links stay private from search indexing. Visit counts are
          aggregate and include link previews; no IP or device details are
          collected for form links.
        </p>
      </div>
      <section
        className="glass-card p-5 space-y-4"
        data-tour="forms-link-manage"
      >
        <div className="flex justify-between items-center gap-3">
          <div>
            <h2 className="font-medium text-sm">Your short links</h2>
            <p className="text-xs text-glass-400 mt-1">
              {active.length} active · {retired.length} retired
            </p>
          </div>
          <button
            className="btn-ghost text-xs"
            onClick={() => void refresh()}
            disabled={loading || busy}
          >
            <RefreshCw className="w-3.5 h-3.5" />
            Refresh
          </button>
        </div>
        {error && (
          <p role="alert" className="text-red-400 text-sm">
            {error}
          </p>
        )}
        {notice && (
          <p
            role="status"
            className="text-sm text-accent-300 flex gap-2 items-start break-all"
          >
            <Check className="w-4 h-4 shrink-0 mt-0.5" />
            {notice}
          </p>
        )}
        {loading ? (
          <p className="py-8 text-center text-sm text-glass-400">
            Loading links…
          </p>
        ) : (
          <>
            {active.length ? (
              active.map(card)
            ) : (
              <div className="py-10 text-center">
                <Link2 className="w-7 h-7 mx-auto text-glass-400 mb-3" />
                <p className="text-sm">No active short links yet</p>
                <p className="text-xs text-glass-400 mt-2">
                  Create one for your next invitation.
                </p>
              </div>
            )}
            {retired.length > 0 && (
              <details>
                <summary className="text-xs text-glass-400 cursor-pointer">
                  Retired links ({retired.length})
                </summary>
                <div className="space-y-3 mt-3">{retired.map(card)}</div>
              </details>
            )}
          </>
        )}
      </section>
    </div>
  );
}
